import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookmarkPlus, Building2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

// One-tap "Save to Business Directory" for a booked pickup/drop-off address (admin only).
export default function SaveToDirectoryButton({ address, which, instructions = "" }) {
  const [existing, setExisting] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!address?.line1) return;
    let ignore = false;
    api.get("/businesses/match", { params: { line1: address.line1, city: address.city } })
      .then((r) => { if (!ignore) setExisting(r.data.business); }).catch(() => { if (!ignore) setExisting(null); });
    return () => { ignore = true; };
  }, [address?.line1, address?.city]);

  if (!address?.line1 || existing === undefined) return null;
  if (existing) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700" data-testid={`in-directory-${which}`}><Check className="h-3 w-3" /> In directory as {existing.trade_name}</span>;
  }

  const save = async (tradeName) => {
    setBusy(true);
    try {
      const { data } = await api.post("/businesses", {
        trade_name: tradeName, abn: "",
        address: { line1: address.line1, line2: address.line2 || "", city: address.city, postcode: address.postcode || "", lat: address.lat ?? null, lng: address.lng ?? null, label: address.label || `${address.line1}, ${address.city} ${address.postcode || ""}`.trim() },
        contact_name: address.contact_name || "", contact_phone: address.contact_phone || "", instructions: which === "dropoff" ? instructions || "" : "",
      });
      setExisting(data);
      setOpen(false);
      toast.success(`${data.trade_name} saved to the Business Directory`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save"); } finally { setBusy(false); }
  };

  const tap = () => {
    if (address.business && address.business.trim().length >= 2) return save(address.business.trim());
    setName(address.contact_name || "");
    setOpen(true);
  };

  return (
    <>
      <button type="button" onClick={tap} disabled={busy} data-testid={`save-to-directory-${which}`}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-kilted-green hover:underline disabled:opacity-50">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookmarkPlus className="h-3 w-3" />} Save to directory
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-sm" data-testid="save-to-directory-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-kilted-gold" /> Add to Business Directory</DialogTitle>
            <DialogDescription>{address.line1}, {address.city} {address.postcode}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Trade / business name</Label>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cooma Hospital" data-testid="save-to-directory-name" />
            {(address.contact_name || address.contact_phone) && <p className="text-xs text-muted-foreground">Contact {address.contact_name}{address.contact_phone ? ` · ${address.contact_phone}` : ""} will be saved too.</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={() => save(name.trim())} disabled={busy || name.trim().length < 2} data-testid="save-to-directory-confirm" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookmarkPlus className="h-4 w-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
